import { API } from "@swivl-mono/api-helper";
import { PhoneNumberHelper, filterClasses } from "@swivl-mono/helpers";
import { AGENT_TEXT_MAX_CHARS } from "@swivl-mono/types";
import { Button, ButtonSize, ButtonVariant, Modal, PhoneInput, Spinner, TextArea } from "@swivl-mono/ui";
import { useEffect, useState } from "react";
import { FiExternalLink } from "react-icons/fi";
import { useLocation, useNavigate } from "react-router-dom";

import { CustomerPickerModal, SelectedCustomer } from "../../../Components/CustomerPickerModal/CustomerPickerModal";
import ContactCenterV3Model from "../../../Models/ContactCenterV3/ContactCenterV3.Store";
import { Member } from "../../../Models/Member/Member.Current";
import { SegmentedToggle } from "../../Widgets/SegmentedToggle";
import { OutboundNumberSelection, SelectOutboundNumberModal } from "./SelectOutboundNumberModal";

type CallToMode = "customer" | "direct";
type ConversationKind = "call" | "text";

const KIND_OPTIONS: { value: ConversationKind; label: string }[] = [
  { value: "call", label: "Call" },
  { value: "text", label: "Text" }
];

interface NewOutboundCallModalProps {
  open: boolean;
  onClose: () => void;
  /** Which choice the modal opens on (ENG-3592). Default: Call. */
  initialKind?: ConversationKind;
  /** A customer to start with — the empty SMS tab opens the modal for its customer (ENG-3592). */
  initialCustomer?: SelectedCustomer | null;
}

/**
 * "New Conversation" (ENG-3018 call, ENG-3592 text; Figma 903-18929 / 1273-19427
 * for the call): call or text a directory customer or any number. From defaults
 * to the server-resolved "Preferred" number (customer location → agent default →
 * org primary); picking one explicitly overrides it for this conversation only.
 * Direct dial to a known number takes the same path as picking that customer;
 * unknown numbers get a user created first, and either way the agent lands on
 * that customer's conversation. A text is sent from the number shown and the
 * agent takes the new SMS thread over.
 */
export function NewOutboundCallModal({ open, onClose, initialKind = "call", initialCustomer = null }: NewOutboundCallModalProps): React.ReactNode {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [kind, setKind] = useState<ConversationKind>(initialKind);
  const [mode, setMode] = useState<CallToMode>("customer");
  const [customer, setCustomer] = useState<SelectedCustomer | null>(initialCustomer);
  const [message, setMessage] = useState("");
  const [directNumber, setDirectNumber] = useState("");
  const [preferred, setPreferred] = useState<{ identifier: string | null; name: string | null } | null>(null);
  const [explicitFrom, setExplicitFrom] = useState<OutboundNumberSelection | null>(null);
  const [customerPickerOpen, setCustomerPickerOpen] = useState(false);
  const [numberPickerOpen, setNumberPickerOpen] = useState(false);
  const [placing, setPlacing] = useState(false);

  // The "Preferred" number is what the live call path would resolve to for
  // this target — recomputed when the selected customer changes (their
  // location can drive it).
  const customerId = mode === "customer" ? customer?.id : undefined;
  useEffect(() => {
    if (!open || !Member.orgId || !Member.id) return;
    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        const result = await API.ContactCenterV3.getPreferredFromNumber(Member.orgId!, Member.id!, customerId);
        if (!cancelled) setPreferred(result);
      } catch {
        if (!cancelled) setPreferred(null);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [open, customerId]);

  if (!open) return null;

  const toNumber = mode === "customer" ? (customer?.phone ?? "") : directNumber;
  const directNumberValid = !!PhoneNumberHelper.isPhoneNumberValid(directNumber);
  const directNumberError = mode === "direct" && directNumber.trim() !== "" && !directNumberValid ? "Enter a valid phone number." : undefined;
  const canPlace = !placing && (mode === "customer" ? !!customer?.phone : directNumberValid);
  const isText = kind === "text";
  const canSendText = canPlace && message.trim() !== "";
  // Text only: a picked customer with no number can't be texted — say so instead of a dead button.
  const customerPhoneMissing = isText && mode === "customer" && !!customer && !customer.phone;
  const fromDisplay =
    explicitFrom ?? (preferred?.identifier ? { identifier: preferred.identifier, name: preferred.name ?? preferred.identifier } : null);

  const handleSendText = async (): Promise<void> => {
    if (!canSendText) return;
    setPlacing(true);
    const result = await ContactCenterV3Model.startOutboundText({
      userId: mode === "customer" ? customer?.id : undefined,
      toNumber,
      // The number the agent saw; when the preferred lookup failed the server resolves it.
      fromNumber: fromDisplay?.identifier,
      content: message.trim(),
      customerName: mode === "customer" ? customer?.name : undefined
    });
    setPlacing(false);
    if (result?.botConversationId) {
      onClose();
      // Already on this customer's conversation: refresh and show the SMS tab in place. Anywhere
      // else (another customer, Recordings): navigate — the new thread is their newest, so detail
      // loading opens SMS. Decided by the page, not the store: the last customer opened stays
      // selected after the agent leaves Conversations.
      if (pathname === `/contact-center-v3/conversations/${result.userId}`) {
        void ContactCenterV3Model.landOnTextThread(result.userId, result.botConversationId);
      } else {
        navigate(`/contact-center-v3/conversations/${result.userId}`);
      }
    }
  };

  const handlePlaceCall = async (): Promise<void> => {
    if (!canPlace) return;
    setPlacing(true);
    const resultUserId = await ContactCenterV3Model.initiateOutboundCall(
      toNumber,
      mode === "customer" ? customer?.id : undefined,
      explicitFrom?.identifier
    );
    setPlacing(false);
    if (resultUserId !== null) {
      onClose();
      // Land on the Voice tab — the call's conversation lives there; detail
      // loading only picks a default channel when none is selected.
      ContactCenterV3Model.setState({ selectedChannel: "voice" });
      navigate(`/contact-center-v3/conversations/${resultUserId}`);
    }
  };

  const modeCard = (cardMode: CallToMode, title: string, subtitle: string): React.ReactElement => {
    const active = mode === cardMode;
    return (
      <button
        type="button"
        onClick={() => setMode(cardMode)}
        className={filterClasses(
          "flex flex-1 cursor-pointer flex-col gap-1 rounded-md border p-3 text-left transition-colors",
          active ? "border-orange-500 bg-orange-50" : "border-grey-200 bg-white hover:border-grey-300"
        )}
      >
        <span className="flex items-center justify-between">
          <span className={filterClasses("body-sm-alt", active ? "text-black" : "text-grey-800")}>{title}</span>
          <span
            className={filterClasses(
              "inline-block size-4 rounded-full border-2",
              active ? "border-orange-500 bg-orange-500 shadow-[inset_0_0_0_2.5px_white]" : "border-grey-300 bg-white"
            )}
          />
        </span>
        <span className="body-sm text-grey-500">{subtitle}</span>
      </button>
    );
  };

  return (
    <>
      <Modal
        isOpen={open}
        onClose={(isOpen) => !isOpen && onClose()}
        title="New Conversation"
        className="w-[480px] max-w-[calc(100vw-32px)]"
        hasTitleBorder
      >
        <div className="flex flex-col gap-4 p-4">
          <SegmentedToggle options={KIND_OPTIONS} value={kind} onChange={setKind} fullWidth />

          <div className="flex flex-col gap-2">
            <span className="body-sm-alt text-grey-700">{isText ? "Text To" : "Call To"}</span>
            <div className="flex gap-3">
              {modeCard("customer", "Customer", "Search and select from the customer directory")}
              {isText
                ? modeCard("direct", "Direct Number", "Enter any phone number to text directly")
                : modeCard("direct", "Direct Dial", "Enter any phone number to dial directly")}
            </div>
            {mode === "customer" && (
              <div className="flex flex-col gap-1">
                <div className="flex items-center justify-between rounded-md border border-grey-200 px-3 py-2.5">
                  <span className="flex flex-col">
                    <span className="body-sm-alt text-grey-800">{customer?.name ?? "Customer"}</span>
                    {customer?.phone && <span className="body-sm text-grey-500">{customer.phone}</span>}
                  </span>
                  <Button size={ButtonSize.SMALL} variant={ButtonVariant.GHOST} onClick={() => setCustomerPickerOpen(true)}>
                    Select <FiExternalLink size={12} />
                  </Button>
                </div>
                {customerPhoneMissing && <span className="body-xs text-red-700">This customer has no phone number on file.</span>}
              </div>
            )}
            {/* Hidden, not removed, while Customer is picked (ENG-3592): removing it emptied the field
                while the modal still held the typed number, so a call or text could go to a number
                no longer on screen. Kept mounted, switching back shows the number Send would use. */}
            <PhoneInput
              placeholder="555-000-000"
              onChange={setDirectNumber}
              error={directNumberError}
              containerClassName={mode === "direct" ? undefined : "hidden"}
            />
          </div>

          <div className="flex flex-col gap-2">
            <span className="body-sm-alt text-grey-700">{isText ? "Text From" : "Call From"}</span>
            <div className="flex items-center justify-between rounded-md border border-grey-200 px-3 py-2.5">
              <span className="flex flex-col">
                <span className="body-sm-alt text-grey-800">{fromDisplay?.name ?? "Outbound Number"}</span>
                {fromDisplay && (
                  <span className="flex items-center gap-1.5">
                    <span className="body-sm text-grey-500">{fromDisplay.identifier}</span>
                    {!explicitFrom && <span className="rounded bg-teal-50 px-1.5 py-0.5 text-[10px] font-semibold text-teal-700">Preferred</span>}
                  </span>
                )}
              </span>
              <Button size={ButtonSize.SMALL} variant={ButtonVariant.GHOST} onClick={() => setNumberPickerOpen(true)}>
                Select <FiExternalLink size={12} />
              </Button>
            </div>
          </div>

          {isText && (
            <div className="flex flex-col gap-2">
              <TextArea
                label="Message"
                placeholder="Write your text…"
                value={message}
                maxLength={AGENT_TEXT_MAX_CHARS}
                onChange={(e) => setMessage(e.target.value)}
                className="h-28 resize-none"
              />
              <div className="flex justify-between gap-4">
                <span className="body-xs text-grey-600">
                  Replies come to you. After an hour with no messages, the conversation goes back to the AI.
                </span>
                <span className="body-xs shrink-0 text-grey-600">
                  {message.length.toLocaleString("en-US")} / {AGENT_TEXT_MAX_CHARS.toLocaleString("en-US")}
                </span>
              </div>
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-1">
            <Button variant={ButtonVariant.GHOST} onClick={onClose}>
              Cancel
            </Button>
            {isText ? (
              <Button variant={ButtonVariant.PRIMARY} disabled={!canSendText} onClick={() => void handleSendText()}>
                {placing && <Spinner />} {placing ? "Sending…" : "Send Text"}
              </Button>
            ) : (
              <Button variant={ButtonVariant.PRIMARY} disabled={!canPlace} onClick={() => void handlePlaceCall()}>
                {placing && <Spinner />} Place Call
              </Button>
            )}
          </div>
        </div>
      </Modal>

      <CustomerPickerModal
        open={customerPickerOpen}
        onClose={() => setCustomerPickerOpen(false)}
        onSelect={(selected) => {
          setCustomer(selected);
          setCustomerPickerOpen(false);
        }}
        selectedCustomer={customer}
        onRemove={() => setCustomer(null)}
      />

      <SelectOutboundNumberModal
        open={numberPickerOpen}
        onClose={() => setNumberPickerOpen(false)}
        onSelect={setExplicitFrom}
        selectedIdentifier={explicitFrom?.identifier ?? preferred?.identifier}
      />
    </>
  );
}
